import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import moment from "moment";
import {
  handleLogout,
  setShowUpdatePasswordModal,
  profileHandleForm,
  handleOpenCustomerPortal,
  uploadLogo,
  getSubscriptionType,
  subscribeUserRequest,
} from "../login/userSession.redux";
import {
  updatePaymentMethodSubscription,
  updatePaymentMethodSubscriptionDone,
  cancelPluginSubscription,
} from "../plugins/plugin.redux";
import { exportCustomers } from "../bowlers/bowlers.redux";
import { FormAppConfiguration } from "./FormAppConfiguration";
import { UploadLogoForm } from "./UploadLogoForm";
import { ModalPassword } from "../modal/ModalPassword";
import { getPhotoForUserId } from "../../utils/utils";
import UpdatingSpinner from "../ui/UpdatingSpinner";
import { paddleCheckout } from "../../utils/paddleCheckout";
import { LoadingPage } from "../ui/LoadingPage";
import TailSpin from "../ui/Loader";
import PluginList from "../plugins/PluginList";
import { api, apiConfig } from "../api/api";
import ConfirmDialog from "../feedback/ConfirmDialog";
import Notify from "../ui/Notify";

const PAGE_COPY = {
  admin: {
    eyebrow: "Account",
    title: "Admin",
    description:
      "Subscription status, exports, connected devices, and session-level admin controls.",
  },
  billing: {
    eyebrow: "Payments",
    title: "Billing",
    description:
      "Review the live Paddle transaction history tied to this account, along with current billing identifiers.",
  },
  store: {
    eyebrow: "Business profile",
    title: "Store",
    description:
      "Maintain the pro shop identity that powers storefront, receipts, and plugin provisioning.",
  },
  plugins: {
    eyebrow: "Extensions",
    title: "Plugins",
    description:
      "Review installed plugins and subscription-level extensions from the profile side of the product.",
  },
};

const CA_PROVINCES = new Set([
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "ON",
  "PE",
  "QC",
  "SK",
  "NT",
  "NU",
  "YT",
]);

const parseLegacyCityField = ({ city, state, postal_code, country }) => {
  const result = {
    city: city || "",
    state: state || "",
    postal_code: postal_code || "",
    country: country || "",
  };

  if (state || postal_code || country) {
    return result;
  }

  if (!city || !city.includes(",")) {
    return result;
  }

  const match = city.match(/^(.+?),\s*([A-Za-z]{2,3})[,\s]+(.+)$/);
  if (!match) {
    return result;
  }

  const [, parsedCity, parsedState, parsedPostcode] = match;
  const upperState = parsedState.toUpperCase();

  return {
    city: parsedCity.trim(),
    state: upperState,
    postal_code: parsedPostcode.trim(),
    country: CA_PROVINCES.has(upperState) ? "CA" : "US",
  };
};

const formatMinorUnits = (amount, currencyCode = "USD") => {
  if (amount === null || amount === undefined || amount === "") {
    return "-";
  }

  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return "-";
  }

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
    }).format(numericAmount / 100);
  } catch (error) {
    return `${currencyCode || "USD"} ${(numericAmount / 100).toFixed(2)}`;
  }
};

const normalizeBillingTransactionsResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (typeof response === "string") {
    try {
      const parsed = JSON.parse(response);
      return normalizeBillingTransactionsResponse(parsed);
    } catch (error) {
      return null;
    }
  }

  if (Array.isArray(response?.transactions)) {
    return response.transactions;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return null;
};

const humanizeToken = (value) =>
  String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const getBillingIntervalLabel = (interval) => {
  if (!interval) {
    return "";
  }

  const normalized = String(interval).toLowerCase();
  if (normalized === "month") {
    return "month";
  }
  if (normalized === "year") {
    return "year";
  }

  return normalized;
};

const getTransactionOriginLabel = (origin) => {
  const normalized = String(origin || "").toLowerCase();

  if (!normalized) {
    return "-";
  }

  if (normalized === "subscription_recurring") {
    return "Subscription Renewal";
  }

  if (normalized === "web") {
    return "Web Checkout";
  }

  return humanizeToken(normalized);
};

const canPrintTransactionInvoice = (transaction) =>
  Boolean(
    transaction?.invoice_url ||
      transaction?.invoiceUrl ||
      transaction?.receipt_url ||
      transaction?.receiptUrl ||
      (transaction?.id && transaction?.invoice_number)
  );

const getInvoiceErrorMessage = (error) => {
  const rawMessage =
    typeof error === "string" && error
      ? error
      : error?.responseText || error?.message || "";

  const normalized = String(rawMessage).toLowerCase();

  if (
    normalized.includes("not found") ||
    normalized.includes("invoice is not available")
  ) {
    return "Invoice not found for this transaction.";
  }

  if (normalized.includes("unable to load paddle invoice")) {
    return "Unable to load this invoice right now. Please try again.";
  }

  if (error?.responseText) {
    try {
      const parsed = JSON.parse(error.responseText);
      return (
        parsed?.message ||
        parsed?.error ||
        "Unable to open invoice right now."
      );
    } catch (parseError) {
      return "Unable to open invoice right now.";
    }
  }

  if (error?.message) {
    return error.message;
  }

  return "Unable to open invoice right now.";
};

const PageWrap = styled.div`
  --paper: #fffdf8;
  --panel: rgba(255, 255, 255, 0.9);
  --line: rgba(12, 31, 83, 0.1);
  --ink: #18233b;
  --muted: #5b6781;
  width: 100vw;
  min-height: 100%;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding: 0;
  box-sizing: border-box;
  background: #ffffff;

  @media only screen and (max-width: 991px) {
    padding: 20px 16px 88px;
  }
`;

const Shell = styled.div`
  width: 100%;
  padding-left: 304px;
  padding-top: 28px;
  padding-bottom: 120px;
  box-sizing: border-box;
  background: #ffffff;
  min-height: 100vh;

  @media only screen and (max-width: 991px) {
    padding-left: 0;
    padding-top: 0;
    padding-bottom: 0;
    background: transparent;
    min-height: auto;
  }
`;

const Sidebar = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  border-radius: 0;
  padding: 88px 22px 24px;
  box-sizing: border-box;
  overflow-y: auto;
  background: linear-gradient(180deg, #f1f3f6 0%, #e7ebef 100%);
  box-shadow:
    inset -1px 0 0 rgba(12, 31, 83, 0.06);
  backdrop-filter: blur(16px);
  z-index: 3;

  @media only screen and (max-width: 991px) {
    position: static;
    width: auto;
    border-radius: 0;
    padding: 16px 0 10px;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    overflow: visible;
  }
`;

const SidebarTitle = styled.div`
  font: 700 11px/1.2 Montserrat, sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #50607f;
  margin-bottom: 16px;

  @media only screen and (max-width: 991px) {
    padding: 0 16px;
    margin-bottom: 12px;
  }
`;

const SidebarList = styled.div`
  display: grid;
  gap: 10px;

  @media only screen and (max-width: 991px) {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 16px 6px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const MenuButton = styled.button`
  width: 100%;
  border: 1px solid ${(props) => (props.$active ? "#00187e" : "#d8deea")};
  border-radius: 16px;
  padding: 13px 16px;
  text-align: left;
  font: 600 14px/1.2 Poppins, sans-serif;
  color: ${(props) => (props.$active ? "#ffffff" : "#1f2a44")};
  background: ${(props) => (props.$active ? "#00187e" : "#ffffff")};
  box-shadow: none;
  transition: border-color 0.18s ease, background 0.18s ease,
    color 0.18s ease;

  &:hover {
    border-color: #00187e;
    background: ${(props) => (props.$active ? "#00187e" : "#f7f9fd")};
  }

  @media only screen and (max-width: 991px) {
    width: auto;
    flex: 0 0 auto;
    border-radius: 999px;
    white-space: nowrap;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(0, 24, 126, 0) 0%,
    rgba(0, 24, 126, 0.18) 18%,
    rgba(0, 24, 126, 0.18) 82%,
    rgba(0, 24, 126, 0) 100%
  );
  margin: 6px 0;

  @media only screen and (max-width: 991px) {
    width: 1px;
    height: 28px;
    margin: 0 2px;
    flex: 0 0 auto;
    background: linear-gradient(
      180deg,
      rgba(0, 24, 126, 0) 0%,
      rgba(0, 24, 126, 0.18) 18%,
      rgba(0, 24, 126, 0.18) 82%,
      rgba(0, 24, 126, 0) 100%
    );
  }
`;

const Content = styled.main`
  display: grid;
  gap: 18px;
  align-content: start;
  align-items: start;
  width: calc(100vw - 304px);
  min-height: calc(100vh - 56px);
  padding: 22px 24px 32px;
  box-sizing: border-box;

  & > * {
    width: min(960px, 100%);
    margin: 0 auto;
    box-sizing: border-box;
  }

  @media only screen and (max-width: 991px) {
    width: 100%;
    padding: 0;
    background: transparent;
    min-height: auto;

    & > * {
      width: 100%;
    }
  }
`;

const Hero = styled.section`
  border-radius: 32px;
  padding: 28px;
  background: #ffffff;
  box-shadow: 0 24px 56px rgba(8, 29, 75, 0.09);
`;

const Eyebrow = styled.div`
  font: 700 11px/1.2 Montserrat, sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #576987;
  margin-bottom: 10px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  color: #0e1d3d;
  font: 700 34px/1.08 Lato, sans-serif;
`;

const HeroDescription = styled.p`
  margin: 12px 0 0;
  max-width: 760px;
  color: #52607c;
  font: 400 15px/1.6 Poppins, sans-serif;
`;

const Card = styled.section`
  border-radius: 28px;
  padding: ${(props) => (props.$compact ? "24px 24px 18px" : "24px")};
  background: var(--panel);
  box-shadow:
    0 20px 44px rgba(8, 29, 75, 0.08),
    inset 0 0 0 1px rgba(255, 255, 255, 0.75);
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
`;

const CardTitle = styled.h2`
  margin: 0;
  color: var(--ink);
  font: 700 22px/1.15 Lato, sans-serif;
`;

const CardDescription = styled.p`
  margin: 10px 0 0;
  color: var(--muted);
  font: 400 14px/1.6 Poppins, sans-serif;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(0, 24, 126, 0.08);
  color: #00187e;
  font: 700 11px/1 Poppins, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
`;

const ActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ActionButton = styled.button`
  border: 1px solid
    ${(props) =>
      props.$danger ? "#c83a34" : props.$secondary ? "#d8deea" : "#00187e"};
  border-radius: 15px;
  padding: 12px 16px;
  background: ${(props) =>
    props.$danger
      ? "#c83a34"
      : props.$secondary
        ? "#ffffff"
        : "#00187e"};
  color: ${(props) =>
    props.$danger ? "#ffffff" : props.$secondary ? "#00187e" : "#ffffff"};
  font: 700 13px/1.2 Poppins, sans-serif;
  box-shadow: none;
  cursor: pointer;
`;

const SectionStack = styled.div`
  display: grid;
  gap: 18px;
`;

const SummaryValueSmall = styled.div`
  margin-top: 10px;
  color: #142240;
  font: 700 24px/1.15 Lato, sans-serif;
  word-break: break-word;
`;

const KeyValueList = styled.div`
  display: grid;
  gap: 10px;
`;

const KeyValueRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(12, 31, 83, 0.08);

  &:last-child {
    border-bottom: 0;
  }

  @media only screen and (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const KeyLabel = styled.div`
  color: #6b7890;
  font: 700 12px/1.3 Poppins, sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const KeyValue = styled.div`
  color: #18233b;
  font: 500 14px/1.5 Poppins, sans-serif;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryTile = styled.div`
  grid-column: ${(props) => (props.$spanFull ? "1 / -1" : "auto")};
  border-radius: 22px;
  padding: 18px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(12, 31, 83, 0.08);
`;

const SummaryLabel = styled.div`
  color: #6b7890;
  font: 700 11px/1.2 Poppins, sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SummaryValue = styled.div`
  margin-top: 10px;
  color: #142240;
  font: 700 22px/1.15 Lato, sans-serif;
  word-break: break-word;
`;

const SummaryMeta = styled.div`
  margin-top: 8px;
  color: #6b7890;
  font: 400 13px/1.5 Poppins, sans-serif;
  word-break: break-word;
`;

const BillingTable = styled.div`
  display: grid;
  gap: 10px;
`;

const BillingHeader = styled.div`
  display: grid;
  grid-template-columns:
    minmax(140px, 1fr)
    minmax(0, 1.35fr)
    minmax(110px, 0.8fr)
    minmax(0, 0.9fr)
    minmax(110px, 0.85fr)
    minmax(110px, 0.8fr)
    minmax(72px, 0.45fr);
  gap: 12px;
  padding: 0 16px;
  color: #6b7890;
  font: 700 11px/1.2 Poppins, sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media only screen and (max-width: 900px) {
    display: none;
  }
`;

const BillingRow = styled.div`
  display: grid;
  grid-template-columns:
    minmax(140px, 1fr)
    minmax(0, 1.35fr)
    minmax(110px, 0.8fr)
    minmax(0, 0.9fr)
    minmax(110px, 0.85fr)
    minmax(110px, 0.8fr)
    minmax(72px, 0.45fr);
  gap: 12px;
  align-items: center;
  padding: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(12, 31, 83, 0.08);

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BillingHeaderCell = styled.div`
  min-width: 0;
  text-align: left;
`;

const BillingCell = styled.div`
  min-width: 0;
  color: #1d2b48;
  font: 500 13px/1.45 Poppins, sans-serif;
  word-break: break-word;
`;

const BillingTextTruncate = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BillingActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid #d8deea;
  background: #ffffff;
  color: #00187e;
  font: 700 12px/1.2 Poppins, sans-serif;
  width: fit-content;
  cursor: pointer;
  box-shadow: none;
`;

const BillingCellLabel = styled.div`
  display: none;
  color: #6b7890;
  font: 700 11px/1.2 Poppins, sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 4px;

  @media only screen and (max-width: 900px) {
    display: block;
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(0, 24, 126, 0.08);
  color: #00187e;
  font: 700 11px/1 Poppins, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  width: fit-content;
`;

const EmptyState = styled.div`
  border-radius: 22px;
  padding: 18px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px dashed rgba(12, 31, 83, 0.14);
  color: #5b6781;
  font: 400 14px/1.6 Poppins, sans-serif;
`;

const NativeSelect = styled.select`
  width: 100%;
  max-width: none;
  min-height: 48px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border-radius: 14px;
  border: 1px solid rgba(0, 24, 126, 0.16);
  padding: 12px 44px 12px 14px;
  background: #ffffff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%2352607c' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M1 1.25 6 6.25l5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 18px center;
  background-size: 12px 8px;
  color: #18233b;
  font: 500 14px/1.4 Poppins, sans-serif;
  box-sizing: border-box;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #142240;
  font: 700 18px/1.2 Lato, sans-serif;
`;

const SectionHint = styled.p`
  margin: 8px 0 0;
  color: #6b7890;
  font: 400 13px/1.55 Poppins, sans-serif;
`;

const SectionBlock = styled.div`
  display: grid;
  gap: 14px;
`;

const SpinnerContainer = styled.div`
  overflow: hidden;
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999 !important;
`;

const ProfileRedux = (props) => {
  const subscriptionTypeRef = useRef();
  const {
    initialValues,
    proshopName,
    proshopLogo,
    proshopAddress,
    proshopCity,
    proshopState,
    proshopPostalCode,
    proshopCountry,
    proshopPhone,
    proshopEmail,
    subscription,
    subscriptionInfo,
    handleLogout,
    setShowUpdatePasswordModal,
    profileHandleForm,
    proshop,
    versionNumber,
    handleOpenCustomerPortal,
    uploadLogo,
    userId,
    isPaddleSubscription,
    updatePaymentMethodSubscription,
    updatePaymentMethodSubscriptionDone,
    cancelPluginSubscription,
    updating,
    isSandbox,
    canceling,
    subscriptionTypeByCountry,
    getSubscriptionType,
    subscribeUserRequest,
    locale,
    exportCustomers,
    registeringUser,
  } = props;

  useEffect(() => {
    getSubscriptionType();
  }, [getSubscriptionType]);

  const navigate = useNavigate();
  const [t] = useTranslation();
  const getPhoto = getPhotoForUserId(userId);
  const logoImg = getPhoto(proshopLogo);
  // ?section=plugins lets anything outside this screen — the navbar plug
  // icon, a link in an email — open the right tab instead of always landing
  // on Admin.
  const [searchParams] = useSearchParams();
  const requestedSection = searchParams.get("section");
  const [activeSection, setActiveSection] = useState(
    requestedSection || "admin"
  );

  useEffect(() => {
    if (requestedSection) {
      setActiveSection(requestedSection);
    }
  }, [requestedSection]);
  const [billingTransactions, setBillingTransactions] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [invoiceError, setInvoiceError] = useState("");
  const [printingInvoiceId, setPrintingInvoiceId] = useState("");
  const [billingFallbackSubscriptionId, setBillingFallbackSubscriptionId] =
    useState("");
  // { type, preview } while the user is being asked to confirm a plan change.
  const [planChange, setPlanChange] = useState(null);
  const [planChangeBusy, setPlanChangeBusy] = useState(false);

  const parsedAddress = parseLegacyCityField({
    city: proshopCity,
    state: proshopState,
    postal_code: proshopPostalCode,
    country: proshopCountry,
  });

  const legacyMigratedRef = useRef(false);
  useEffect(() => {
    if (!proshop?.id || legacyMigratedRef.current) {
      return;
    }

    const changed =
      parsedAddress.city !== (proshopCity || "") ||
      parsedAddress.state !== (proshopState || "") ||
      parsedAddress.postal_code !== (proshopPostalCode || "") ||
      parsedAddress.country !== (proshopCountry || "");

    if (changed) {
      legacyMigratedRef.current = true;
      profileHandleForm({
        proshop: { ...proshop, ...parsedAddress },
        app_user: { ...initialValues, ...parsedAddress },
      });
    }
  }, [
    initialValues,
    parsedAddress,
    profileHandleForm,
    proshop,
    proshopCity,
    proshopCountry,
    proshopPostalCode,
    proshopState,
  ]);

  const handleProfile = (values) => {
    profileHandleForm({
      proshop: { ...proshop, ...values },
      app_user: { ...initialValues, ...values },
    });
  };

  const handleLogoutClick = () => {
    navigate("/login", { replace: true });
    handleLogout();
  };

  const handlePrintInvoice = (transaction) => {
    if (!transaction?.id) {
      setInvoiceError("This transaction does not have a valid Paddle ID.");
      return;
    }

    setInvoiceError("");
    setPrintingInvoiceId(transaction.id);

    api.ajax(`api/subscriptiontype/paddle/invoice/${encodeURIComponent(transaction.id)}`, {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "GET",
      data: {
        isSandbox,
        disposition: "inline",
        redirect: "false",
      },
    })
      .then((response) => {
        const invoiceUrl = response?.url || response?.data?.url;

        if (!invoiceUrl) {
          setInvoiceError("Invoice URL was not returned for this transaction.");
          return;
        }

        if (typeof window !== "undefined") {
          window.open(invoiceUrl, "_blank", "noopener,noreferrer");
        }
      })
      .catch((error) => {
        const message = getInvoiceErrorMessage(error);
        setInvoiceError(message);

        if (typeof window !== "undefined" && typeof window.alert === "function") {
          window.alert(message);
        }
      })
      .finally(() => {
        setPrintingInvoiceId("");
      });
  };

  const rawSubscriptionInfo = useMemo(() => {
    const candidates = [
      subscription?.subscription_info,
      subscription?.subscriptionInfo,
      initialValues?.subscription?.subscription_info,
      initialValues?.subscription?.subscriptionInfo,
    ];

    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }

      if (typeof candidate === "object") {
        return candidate;
      }

      if (typeof candidate === "string") {
        try {
          return JSON.parse(candidate);
        } catch (error) {
          return {};
        }
      }
    }

    return {};
  }, [initialValues, subscription]);

  const paddleCustomerId =
    subscriptionInfo?.customer_id ||
    subscriptionInfo?.customer ||
    rawSubscriptionInfo?.customer_id ||
    rawSubscriptionInfo?.customer ||
    subscription?.plan_id ||
    subscription?.planId ||
    initialValues?.subscription?.plan_id ||
    initialValues?.subscription?.planId ||
    "";
  const paddleSubscriptionId =
    subscriptionInfo?.id ||
    subscriptionInfo?.subscription_id ||
    rawSubscriptionInfo?.id ||
    rawSubscriptionInfo?.subscription_id ||
    subscription?.subscription_id ||
    subscription?.subscriptionId ||
    initialValues?.subscription?.subscription_id ||
    initialValues?.subscription?.subscriptionId ||
    "";
  const resolvedPaddleSubscriptionId =
    paddleSubscriptionId || billingFallbackSubscriptionId;

  const navItems = useMemo(() => {
    return [
      { id: "admin", label: "Admin" },
      { id: "billing", label: "Billing" },
      { id: "store", label: "Store" },
      { id: "plugins", label: "Plugins" },
    ];
  }, []);

  useEffect(() => {
    const validSections = navItems.filter((item) => item.type !== "divider");
    if (!validSections.some((item) => item.id === activeSection)) {
      setActiveSection(validSections[0]?.id || "admin");
    }
  }, [activeSection, navItems]);

  const currentPage = PAGE_COPY[activeSection] || PAGE_COPY.admin;

  useEffect(() => {
    if (!isPaddleSubscription || billingLoading || billingLoaded) {
      return;
    }

    let cancelled = false;
    let timeoutId;
    setBillingLoading(true);
    setBillingError("");

    const loadBilling = async () => {
      let fallbackSubscriptionId = billingFallbackSubscriptionId;

      if (!paddleCustomerId && !resolvedPaddleSubscriptionId && userId) {
        const subscriptionStatus = await api.ajax("getSubscriptionStatus", {
          noWorker: true,
          url: apiConfig.appUrl,
          type: "GET",
          params: { userId },
        });

        fallbackSubscriptionId = subscriptionStatus?.subscriptionId || "";
        if (!cancelled && fallbackSubscriptionId) {
          setBillingFallbackSubscriptionId(fallbackSubscriptionId);
        }
      }

      if (!paddleCustomerId && !fallbackSubscriptionId && !resolvedPaddleSubscriptionId) {
        if (!cancelled) {
          setBillingTransactions([]);
          setBillingLoaded(true);
          setBillingError(
            "No Paddle customer or subscription ID is available for this account yet."
          );
        }
        return;
      }

      return Promise.race([
        api.ajax("api/subscriptiontype/paddle", {
          noWorker: true,
          url: apiConfig.appUrl,
          type: "GET",
          data: {
            customerId: paddleCustomerId || undefined,
            subscriptionId:
              !paddleCustomerId
                ? fallbackSubscriptionId || resolvedPaddleSubscriptionId || undefined
                : undefined,
            isSandbox,
            limit: 60,
            historyYears: 10,
          },
        }),
        new Promise((_, reject) => {
          timeoutId = window.setTimeout(() => {
            reject(new Error("Timed out loading Paddle transaction history."));
          }, 120000);
        }),
      ]);
    };

    Promise.resolve(loadBilling())
      .then((response) => {
        if (cancelled) {
          return;
        }

        const transactions = normalizeBillingTransactionsResponse(response);

        if (!Array.isArray(transactions)) {
          setBillingError(
            "Billing response did not include transactions. Confirm the request is being sent with a Paddle customer or subscription ID."
          );
          setBillingTransactions([]);
          setBillingLoaded(true);
          return;
        }

        setBillingError("");
        setBillingTransactions(transactions);
        setBillingLoaded(true);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setBillingError(
          error?.message || "Unable to load Paddle transaction history."
        );
        setBillingLoaded(true);
      })
      .finally(() => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
        if (!cancelled) {
          setBillingLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    billingFallbackSubscriptionId,
    billingLoaded,
    billingLoading,
    isPaddleSubscription,
    isSandbox,
    paddleCustomerId,
    resolvedPaddleSubscriptionId,
    userId,
  ]);

  useEffect(() => {
    setBillingLoading(false);
    setBillingLoaded(false);
    setBillingTransactions([]);
    setBillingError("");
    setInvoiceError("");
    setPrintingInvoiceId("");
    setBillingFallbackSubscriptionId("");
  }, [isPaddleSubscription, paddleCustomerId, paddleSubscriptionId]);

  const subscriptionTiming = (() => {
    if (!subscriptionInfo) {
      return "-";
    }

    if (subscription.type.indexOf("PAYPAL") >= 0) {
      return subscriptionInfo.billing_info?.next_billing_time
        ? moment(subscriptionInfo.billing_info.next_billing_time).format(
            "DD/MM/YYYY"
          )
        : "-";
    }

    if (subscription.type.indexOf("STRIPE") >= 0) {
      return subscriptionInfo.current_period_end
        ? moment.unix(subscriptionInfo.current_period_end).format("DD/MM/YYYY")
        : "-";
    }

    if (subscription.type.indexOf("PADDLE") >= 0) {
      if (subscriptionInfo.next_billed_at) {
        return moment(subscriptionInfo.next_billed_at).format("DD/MM/YYYY");
      }

      return subscriptionInfo?.current_billing_period?.ends_at
        ? moment(subscriptionInfo.current_billing_period.ends_at).format(
            "DD/MM/YYYY"
          )
        : "-";
    }

    return "-";
  })();

  const billingLabel =
    subscription.type.indexOf("PADDLE") >= 0 && !subscriptionInfo.next_billed_at
      ? t("form.label.ends_at")
      : t("form.label.next_billing");

  const visibleSubscriptionTypes = useMemo(
    () =>
      subscriptionTypeByCountry.filter(
        (type) => type.custom_data?.hide_in_profile !== "true"
      ),
    [subscriptionTypeByCountry]
  );

  const subscriptionStatusValue = String(
    subscriptionInfo?.status || "unknown"
  ).toLowerCase();
  const currentSubscriptionPrice =
    subscriptionInfo?.items?.[0]?.price || rawSubscriptionInfo?.items?.[0]?.price;
  const currentSubscriptionPriceId =
    currentSubscriptionPrice?.id ||
    subscriptionInfo?.price_id ||
    rawSubscriptionInfo?.price_id ||
    "";

  const currentSubscriptionType = useMemo(() => {
    const candidateIds = [
      currentSubscriptionPriceId,
      subscription?.marketing_platform_id,
      subscription?.plan_id,
      subscription?.planId,
    ].filter(Boolean);

    return (
      visibleSubscriptionTypes.find((type) =>
        candidateIds.some(
          (id) =>
            String(type.marketing_platform_id) === String(id) ||
            String(type.id) === String(id)
        )
      ) || null
    );
  }, [
    currentSubscriptionPriceId,
    subscription?.marketing_platform_id,
    subscription?.plan_id,
    subscription?.planId,
    visibleSubscriptionTypes,
  ]);

  const currentSubscriptionTransaction = useMemo(() => {
    if (!billingTransactions.length) {
      return null;
    }

    const currentSubscriptionId =
      subscriptionInfo?.id ||
      subscriptionInfo?.subscription_id ||
      resolvedPaddleSubscriptionId;

    return (
      billingTransactions.find(
        (transaction) =>
          currentSubscriptionId &&
          String(transaction.subscription_id) === String(currentSubscriptionId)
      ) || billingTransactions[0]
    );
  }, [billingTransactions, resolvedPaddleSubscriptionId, subscriptionInfo]);

  const currentSubscriptionInterval = useMemo(() => {
    const intervalFromPrice = getBillingIntervalLabel(
      currentSubscriptionPrice?.billing_cycle?.interval
    );

    if (intervalFromPrice) {
      return intervalFromPrice;
    }

    if (currentSubscriptionType?.description?.indexOf("YEARLY") >= 0) {
      return "year";
    }

    return currentSubscriptionType ? "month" : "";
  }, [currentSubscriptionPrice, currentSubscriptionType]);

  const currentSubscriptionAmount = useMemo(() => {
    if (currentSubscriptionPrice?.unit_price?.amount !== undefined) {
      return formatMinorUnits(
        currentSubscriptionPrice.unit_price.amount,
        currentSubscriptionPrice.unit_price.currency_code
      );
    }

    if (currentSubscriptionType?.price !== undefined) {
      return formatMinorUnits(
        Math.round(Number(currentSubscriptionType.price) * 100),
        subscriptionInfo?.currency_code ||
          currentSubscriptionTransaction?.currency_code ||
          "USD"
      );
    }

    if (currentSubscriptionTransaction?.total !== undefined) {
      return formatMinorUnits(
        currentSubscriptionTransaction.total,
        currentSubscriptionTransaction.currency_code
      );
    }

    return "-";
  }, [
    currentSubscriptionPrice,
    currentSubscriptionTransaction,
    currentSubscriptionType,
    subscriptionInfo?.currency_code,
  ]);

  const currentSubscriptionPlanName =
    currentSubscriptionType?.description ||
    currentSubscriptionPrice?.name ||
    humanizeToken(subscription?.type?.replace("_SANDBOX", "")) ||
    "Subscription";
  const currentSubscriptionStatus = humanizeToken(subscriptionInfo?.status || "unknown");
  const canManageRecurringSubscription =
    isPaddleSubscription &&
    Boolean(subscriptionInfo?.id) &&
    !["canceled", "cancelled"].includes(subscriptionStatusValue);
  const canChangePaddlePlan =
    isPaddleSubscription &&
    Boolean(subscriptionInfo?.customer_id) &&
    visibleSubscriptionTypes.length > 0;

  const requestPlanChange = (type, preview) =>
    api.ajax("changeSubscriptionPlanPaddle", {
      noWorker: true,
      url: apiConfig.appUrl,
      type: "POST",
      data: { priceId: type.marketing_platform_id, isSandbox, preview },
    });

  const planChangeFailed = (error) => {
    let code = "";
    try {
      code = JSON.parse(error.response).error;
    } catch (e) {
      // not a JSON answer; the generic message covers it
    }
    Notify.error(
      code === "same_plan"
        ? t("planChange.samePlan")
        : t("planChange.failed")
    );
  };

  const previewPlanChange = (type) => {
    if (planChangeBusy) return;
    setPlanChangeBusy(true);
    requestPlanChange(type, true)
      .then((preview) => setPlanChange({ type, preview }))
      .catch(planChangeFailed)
      .finally(() => setPlanChangeBusy(false));
  };

  const confirmPlanChange = () => {
    if (!planChange || planChangeBusy) return;
    setPlanChangeBusy(true);
    requestPlanChange(planChange.type, false)
      .then(() => {
        setPlanChange(null);
        Notify.success(t("planChange.done"));
        setTimeout(() => window.location.reload(), 1500);
      })
      .catch((error) => {
        setPlanChange(null);
        planChangeFailed(error);
      })
      .finally(() => setPlanChangeBusy(false));
  };

  const planChangeAmount = planChange?.preview?.amount
    ? `${(Math.abs(Number(planChange.preview.amount)) / 100).toFixed(2)} ${
        planChange.preview.currency || ""
      }`.trim()
    : "";

  const handleStartSubscription = () => {
    const selectedType = visibleSubscriptionTypes.find(
      (type) => String(type.id) === String(subscriptionTypeRef.current?.value)
    );

    if (!selectedType) {
      return;
    }

    // A live subscription is switched in place. A checkout for the new price
    // would buy a second subscription and leave this one billing.
    if (canManageRecurringSubscription) {
      previewPlanChange(selectedType);
      return;
    }

    paddleCheckout({
      items: [
        {
          priceId: selectedType.marketing_platform_id,
          quantity: 1,
        },
      ],
      customer: {
        id: subscriptionInfo.customer_id,
        address: subscriptionInfo.address_id
          ? { id: subscriptionInfo.address_id }
          : undefined,
      },
      onComplete: (event) => {
        subscribeUserRequest({
          subscriptionType: selectedType.marketing_platform,
          customer: event.data.customer.id,
          transactionId: event.data.transaction_id,
          user_id: userId,
          isSandbox,
        });
      },
      settings: {
        locale,
      },
      isSandbox,
    });
  };

  if (registeringUser) {
    return (
      <SpinnerContainer>
        <LoadingPage loadingMessage={"general.subscribing"} />
        <TailSpin />
      </SpinnerContainer>
    );
  }

  return (
    <PageWrap>
      <Shell>
        <Sidebar>
          <SidebarTitle>Profile menu</SidebarTitle>
          <SidebarList>
            {navItems.map((item) =>
              item.type === "divider" ? (
                <MenuDivider key={item.id} />
              ) : (
                <MenuButton
                  key={item.id}
                  type="button"
                  $active={activeSection === item.id}
                  onClick={() => setActiveSection(item.id)}
                >
                  {item.label}
                </MenuButton>
              )
            )}
          </SidebarList>
        </Sidebar>

        <Content>
          <Hero>
            <Eyebrow>{currentPage.eyebrow}</Eyebrow>
            <HeroTitle>{currentPage.title}</HeroTitle>
            <HeroDescription>{currentPage.description}</HeroDescription>
          </Hero>

            {activeSection === "admin" && (
              <>
                <Card $compact>
                  <CardHead>
                    <div>
                      <CardTitle>Account summary</CardTitle>
                      <CardDescription>
                        Membership status and billing checkpoints for this cloud
                        account.
                      </CardDescription>
                    </div>
                    <Tag>v{(versionNumber * 1).toFixed(3)}</Tag>
                  </CardHead>

                  <KeyValueList>
                    <KeyValueRow>
                      <KeyLabel>{t("form.label.username")}</KeyLabel>
                      <KeyValue>{initialValues?.username || "-"}</KeyValue>
                    </KeyValueRow>
                    <KeyValueRow>
                      <KeyLabel>{t("form.label.member_as")}</KeyLabel>
                      <KeyValue>
                        {initialValues?.created_at
                          ? moment(initialValues.created_at).format("DD/MM/YYYY")
                          : "-"}
                      </KeyValue>
                    </KeyValueRow>
                    <KeyValueRow>
                      <KeyLabel>{billingLabel}</KeyLabel>
                      <KeyValue>{subscriptionTiming}</KeyValue>
                    </KeyValueRow>
                    <KeyValueRow>
                      <KeyLabel>{t("form.label.subscriptionStatus")}</KeyLabel>
                      <KeyValue>
                        {(subscriptionInfo?.status || "UNKNOWN").toUpperCase()}
                      </KeyValue>
                    </KeyValueRow>
                  </KeyValueList>
                </Card>

                <Card $compact>
                  <CardHead>
                    <div>
                      <CardTitle>Admin actions</CardTitle>
                      <CardDescription>
                        Password, exports, connected devices, and account exit
                        actions.
                      </CardDescription>
                    </div>
                  </CardHead>

                  <ActionBar>
                    <ActionButton
                      type="button"
                      $secondary
                      onClick={() => setShowUpdatePasswordModal({ show: true })}
                    >
                      {t("form.label.change_password")}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      $secondary
                      onClick={() => exportCustomers()}
                    >
                      {t("form.label.export_bowlers")}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      $secondary
                      onClick={() => navigate("/devices")}
                    >
                      Connected Devices
                    </ActionButton>
                    <ActionButton type="button" $secondary onClick={handleLogoutClick}>
                      {t("form.label.logout")}
                    </ActionButton>
                  </ActionBar>
                </Card>
              </>
            )}

            {activeSection === "billing" && (
              <>
                <Card>
                  <CardHead>
                    <div>
                      <CardTitle>Billing summary</CardTitle>
                      <CardDescription>
                        Current subscription status, renewal timing, payment
                        amount, and account billing actions.
                      </CardDescription>
                    </div>
                  </CardHead>

                  <SummaryGrid>
                    <SummaryTile $spanFull>
                      <SummaryLabel>Current plan</SummaryLabel>
                      <SummaryValueSmall>{currentSubscriptionPlanName}</SummaryValueSmall>
                      <SummaryMeta>
                        {currentSubscriptionAmount !== "-"
                          ? `Current recurring amount: ${currentSubscriptionAmount}${
                              currentSubscriptionInterval
                                ? ` / ${currentSubscriptionInterval}`
                                : ""
                            }`
                          : "Current recurring amount unavailable."}
                      </SummaryMeta>
                    </SummaryTile>
                    <SummaryTile>
                      <SummaryLabel>Subscription status</SummaryLabel>
                      <SummaryValue>
                        {currentSubscriptionStatus.toUpperCase()}
                      </SummaryValue>
                      <SummaryMeta>
                        {canManageRecurringSubscription
                          ? "This subscription can still be managed."
                          : "This subscription is not currently renewable."}
                      </SummaryMeta>
                    </SummaryTile>
                    <SummaryTile>
                      <SummaryLabel>{billingLabel}</SummaryLabel>
                      <SummaryValue>{subscriptionTiming}</SummaryValue>
                      <SummaryMeta>
                        {subscriptionInfo?.next_billed_at
                          ? "Next payment due date from the active Paddle subscription."
                          : "If the subscription will not renew, this is the end date."}
                      </SummaryMeta>
                    </SummaryTile>
                    <SummaryTile>
                      <SummaryLabel>Amount due</SummaryLabel>
                      <SummaryValue>{currentSubscriptionAmount}</SummaryValue>
                      <SummaryMeta>
                        {currentSubscriptionInterval
                          ? `Recurring ${currentSubscriptionInterval} charge.`
                          : "Current recurring amount for this subscription."}
                      </SummaryMeta>
                    </SummaryTile>
                  </SummaryGrid>
                </Card>

                <Card>
                  <CardHead>
                    <div>
                      <CardTitle>Subscription actions</CardTitle>
                      <CardDescription>
                        Update payment details, change the plan, or cancel the
                        current subscription.
                      </CardDescription>
                    </div>
                  </CardHead>

                  <SectionStack>
                    <ActionBar>
                      {!isPaddleSubscription && (
                        <ActionButton
                          type="button"
                          $secondary
                          onClick={() => handleOpenCustomerPortal()}
                        >
                          {t("form.label.manage_payment")}
                        </ActionButton>
                      )}

                      {canManageRecurringSubscription && (
                        <ActionButton
                          type="button"
                          $secondary
                          onClick={() => {
                            updatePaymentMethodSubscription();
                            paddleCheckout({
                              updatePaymentMethodSubscription: true,
                              subscriptionId: subscriptionInfo.id,
                              isSandbox,
                              onClose: () => {
                                updatePaymentMethodSubscriptionDone();
                              },
                            });
                          }}
                        >
                          {updating ? (
                            <UpdatingSpinner width={30} height={30} fill="#00187e" />
                          ) : (
                            t("form.label.update_payment_method")
                          )}
                        </ActionButton>
                      )}

                      {canManageRecurringSubscription && (
                        <ActionButton
                          type="button"
                          $danger
                          onClick={() =>
                            cancelPluginSubscription({
                              subscriptionId: subscriptionInfo.id,
                            })
                          }
                        >
                          {canceling ? (
                            <UpdatingSpinner width={30} height={30} fill="#ffffff" />
                          ) : (
                            t("form.label.cancel")
                          )}
                        </ActionButton>
                      )}
                    </ActionBar>

                    {canChangePaddlePlan && (
                      <SectionBlock>
                        <div>
                          <SectionTitle>Change plan</SectionTitle>
                          <SectionHint>
                            Select a different subscription and start the Paddle
                            checkout flow to switch plans.
                          </SectionHint>
                        </div>
                        <NativeSelect
                          ref={subscriptionTypeRef}
                          defaultValue={visibleSubscriptionTypes[0]?.id || ""}
                        >
                          {visibleSubscriptionTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.description} - $ {type.price}
                            </option>
                          ))}
                        </NativeSelect>
                        <ActionBar>
                          <ActionButton type="button" onClick={handleStartSubscription}>
                            {planChangeBusy && !planChange ? (
                              <UpdatingSpinner width={30} height={30} fill="#ffffff" />
                            ) : (
                              t("form.label.update_subscription")
                            )}
                          </ActionButton>
                        </ActionBar>
                        <ConfirmDialog
                          show={Boolean(planChange)}
                          onHide={() => !planChangeBusy && setPlanChange(null)}
                          severity="warning"
                          title={t("planChange.title")}
                          description={t(
                            planChange?.preview?.action === "credit"
                              ? "planChange.confirmCredit"
                              : "planChange.confirmCharge",
                            {
                              plan: planChange?.type?.description,
                              amount: planChangeAmount,
                            }
                          )}
                          confirmText={t("planChange.confirm")}
                          onConfirm={confirmPlanChange}
                        />
                      </SectionBlock>
                    )}
                  </SectionStack>
                </Card>

                <Card>
                  <CardHead>
                    <div>
                      <CardTitle>Transaction history</CardTitle>
                      <CardDescription>
                        All Paddle transactions currently available for this
                        customer, ordered from newest to oldest.
                      </CardDescription>
                    </div>
                    <Tag>
                      {!billingLoaded || billingLoading
                        ? "Loading"
                        : `${billingTransactions.length} records`}
                    </Tag>
                  </CardHead>

                  {!isPaddleSubscription && (
                    <EmptyState>
                      Billing history in this section is currently available for
                      Paddle accounts only.
                    </EmptyState>
                  )}

                  {isPaddleSubscription && billingLoading && (
                    <EmptyState>
                      Loading Paddle transaction history in the background.
                    </EmptyState>
                  )}

                  {isPaddleSubscription && !billingLoading && billingError && (
                    <EmptyState>{billingError}</EmptyState>
                  )}

                  {isPaddleSubscription &&
                    !billingLoading &&
                    !billingError &&
                    billingLoaded &&
                    billingTransactions.length === 0 && (
                      <EmptyState>
                        No Paddle transactions were returned for this account yet.
                      </EmptyState>
                    )}

                  {isPaddleSubscription &&
                    !billingLoading &&
                    !billingError &&
                    billingTransactions.length > 0 && (
                      <BillingTable>
                        <BillingHeader>
                          <BillingHeaderCell>Origin</BillingHeaderCell>
                          <BillingHeaderCell>Transaction ID</BillingHeaderCell>
                          <BillingHeaderCell>Status</BillingHeaderCell>
                          <BillingHeaderCell>Date</BillingHeaderCell>
                          <BillingHeaderCell>Total</BillingHeaderCell>
                          <BillingHeaderCell>Invoice</BillingHeaderCell>
                          <BillingHeaderCell>Print</BillingHeaderCell>
                        </BillingHeader>
                        {billingTransactions.map((transaction) => {
                          return (
                          <BillingRow key={transaction.id}>
                            <BillingCell>
                              <BillingCellLabel>Origin</BillingCellLabel>
                              {getTransactionOriginLabel(transaction.origin)}
                            </BillingCell>
                            <BillingCell>
                              <BillingCellLabel>Transaction ID</BillingCellLabel>
                              <BillingTextTruncate title={transaction.id || "-"}>
                                {transaction.id || "-"}
                              </BillingTextTruncate>
                            </BillingCell>
                            <BillingCell>
                              <BillingCellLabel>Status</BillingCellLabel>
                              <StatusPill>
                                {(transaction.status || "unknown")
                                  .split("_")
                                  .join(" ")}
                              </StatusPill>
                            </BillingCell>
                            <BillingCell>
                              <BillingCellLabel>Date</BillingCellLabel>
                              {transaction.billed_at || transaction.created_at
                                ? moment(
                                    transaction.billed_at || transaction.created_at
                                  ).format("DD/MM/YYYY")
                                : "-"}
                            </BillingCell>
                            <BillingCell>
                              <BillingCellLabel>Total</BillingCellLabel>
                              {formatMinorUnits(
                                transaction.total,
                                transaction.currency_code
                              )}
                            </BillingCell>
                            <BillingCell>
                              <BillingCellLabel>Invoice</BillingCellLabel>
                              {transaction.invoice_number || "-"}
                            </BillingCell>
                            <BillingCell>
                              <BillingCellLabel>Print</BillingCellLabel>
                              {canPrintTransactionInvoice(transaction) ? (
                                <BillingActionButton
                                  type="button"
                                  onClick={() => handlePrintInvoice(transaction)}
                                >
                                  {printingInvoiceId === transaction.id
                                    ? (
                                      <UpdatingSpinner
                                        width={18}
                                        height={18}
                                        fill="#00187e"
                                      />
                                    ) : (
                                      "Print"
                                    )}
                                </BillingActionButton>
                              ) : (
                                "-"
                              )}
                            </BillingCell>
                          </BillingRow>
                        )})}
                      </BillingTable>
                    )}
                </Card>
              </>
            )}

            {activeSection === "store" && (
              <>
                <Card>
                  <CardHead>
                    <div>
                      <CardTitle>Store identity</CardTitle>
                      <CardDescription>
                        Edit the pro shop details used across cloud, storefront,
                        and plugin provisioning flows.
                      </CardDescription>
                    </div>
                  </CardHead>

                  <FormAppConfiguration
                    proshopLabel={t("form.label.pro_shop_name")}
                    proshopName={proshopName}
                    proshopLogo={proshopLogo}
                    proshopAddress={proshopAddress}
                    proshopCity={parsedAddress.city}
                    proshopState={parsedAddress.state}
                    proshopPostalCode={parsedAddress.postal_code}
                    proshopCountry={parsedAddress.country}
                    proshopPhone={proshopPhone}
                    proshopEmail={proshopEmail}
                    addresLabel={t("form.label.address")}
                    cityLabel={t("form.label.city")}
                    stateLabel={t("form.label.state")}
                    postalCodeLabel={t("form.label.postal_code")}
                    countryLabel={t("form.label.country")}
                    phoneLabel={t("form.label.phone")}
                    emailLabel={t("form.label.email")}
                    saveLabel={t("form.label.save")}
                    savedLabel={t("form.label.saved")}
                    onSubmit={handleProfile}
                  />
                </Card>

                <Card>
                  <CardHead>
                    <div>
                      <CardTitle>Brand assets</CardTitle>
                      <CardDescription>
                        Update the logo that appears in Spectre Cloud and
                        customer-facing materials.
                      </CardDescription>
                    </div>
                  </CardHead>

                  <UploadLogoForm
                    uploadButtonLabel={t("form.label.upload_button")}
                    uploadDescription={t("form.description.upload_description")}
                    uploadLabel={t("form.label.upload")}
                    previewLabel={t("form.label.preview")}
                    logo={logoImg}
                    proshop={proshop}
                    uploadLogo={uploadLogo}
                  />
                </Card>
              </>
            )}

            {activeSection === "plugins" && (
              <Card>
                <CardHead>
                  <div>
                    <CardTitle>Installed plugins</CardTitle>
                    <CardDescription>
                      Review, activate, and manage your available Spectre
                      plugins in one place.
                    </CardDescription>
                  </div>
                </CardHead>
                <PluginList />
              </Card>
            )}
          </Content>
        </Shell>

        <ModalPassword />
      </PageWrap>
  );
};

export const ProfileScreen = connect(
  (state) => {
    const isSandbox = state.userSession.isSandbox;
    const subscription = state.userSession.subscription;
    const subscriptionType = state.userSession.subscriptionType.filter(
      (type) => type.marketing_platform === subscription.type
    );
    const subscriptionInfo = state.userSession.subscriptionInfo?.id
      ? state.userSession.subscriptionInfo
      : {
          customer_id: state.userSession.subscription.plan_id,
          status: "CANCELLED",
        };

    const currency = state.userSession.isPaddleSubscription
      ? subscriptionInfo?.currency_code || "USD"
      : subscriptionInfo?.billingCountry === "CA" || subscriptionInfo
        ? "CAD"
        : "USD";

    const subscriptionTypeByCountry = subscriptionType.filter((type) =>
      type.description.indexOf(currency) >= 0
    );

    return {
      initialValues: state.userSession.userData,
      userId: state.userSession.userData.id,
      proshopName: state.userSession.proshop.name,
      proshopLogo: state.userSession.proshop.logo,
      proshopAddress: state.userSession.proshop.address,
      proshopCity: state.userSession.proshop.city,
      proshopCountry: state.userSession.proshop.country,
      proshopState: state.userSession.proshop.state,
      proshopPostalCode: state.userSession.proshop.postal_code,
      proshopPhone: state.userSession.userData.phone,
      proshopEmail: state.userSession.userData.email,
      subscription,
      subscriptionTypeByCountry,
      subscriptionInfo,
      proshop: state.userSession.proshop,
      versionNumber: state.userSession.versionNumber,
      isPaddleSubscription: state.userSession.isPaddleSubscription,
      updating: state.plugin.updating,
      canceling: state.plugin.canceling,
      isSandbox,
      locale: state.settings.language,
      registeringUser: state.userSession.registeringUser,
    };
  },
  {
    handleLogout,
    setShowUpdatePasswordModal,
    profileHandleForm,
    handleOpenCustomerPortal,
    uploadLogo,
    updatePaymentMethodSubscription,
    updatePaymentMethodSubscriptionDone,
    cancelPluginSubscription,
    getSubscriptionType,
    subscribeUserRequest,
    exportCustomers,
  }
)(ProfileRedux);
